import { ComponentProps, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { collectFormErrors } from '../../../../utils/collect-form-errors';
import { Button } from '../../../atoms/button/button';
import { FormError } from '../../../atoms/form-error/form-error';
import { Header } from '../../../atoms/header/header';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';
import { useValidateEmailOrPhone } from '../username-validation';

type Inputs = {
  username: string;
  password: string;
};

const UsernameField = ({ variant, ...props }: ComponentProps<typeof Input>) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="username">{t('auth.common.usernameLabel')}</Label>
      <Input placeholder={t('auth.common.usernamePlaceholder')} type="text" variant={variant} {...props} />
    </div>
  );
};

const PasswordField = ({
  forgotPasswordUrl,
  setShowPassword,
  variant,
  ...props
}: ComponentProps<typeof Input> & {
  readonly forgotPasswordUrl: string;
  readonly setShowPassword: (cb: (s?: boolean) => boolean) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="password">{t('auth.common.passwordLabel')}</Label>
      <Input
        placeholder={t('auth.common.passwordPlaceholder')}
        suffix={
          <Text className="text-neutral-700 cursor-pointer" onClick={() => setShowPassword((s) => !s)} tag="p">
            {t('auth.common.showPassword')}
          </Text>
        }
        variant={variant}
        {...props}
      />
      <a className="underline" href={forgotPasswordUrl}>
        {t('auth.login.forgotPasswordLinkText')}
      </a>
    </div>
  );
};

const FormFields = ({
  errors,
  forgotPasswordUrl,
  passwordInputType,
  register,
  setShowPassword,
  passwordLess,
}: {
  readonly passwordLess: boolean;
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
  readonly forgotPasswordUrl: string;
  readonly passwordInputType: string;
  readonly register: ReturnType<typeof useForm<Inputs>>['register'];
  readonly setShowPassword: (cb: (s?: boolean) => boolean) => void;
}) => {
  const { t } = useTranslation();
  const validate = useValidateEmailOrPhone();

  return (
    <div className="flex flex-col gap-6">
      <Header level="h1">{t('auth.login.welcomeHeader')}</Header>
      <UsernameField
        variant={errors.username ? 'error' : 'default'}
        {...register('username', {
          required: t('auth.common.errors.usernameRequired'),
          validate,
        })}
      />
      {!passwordLess && (
        <PasswordField
          forgotPasswordUrl={forgotPasswordUrl}
          setShowPassword={setShowPassword}
          type={passwordInputType}
          variant={errors.password ? 'error' : 'default'}
          {...register('password', {
            required: t('auth.common.errors.passwordRequired'),
          })}
        />
      )}
    </div>
  );
};

const FormActions = ({
  formErrors,
  isSubmitting,
}: {
  readonly formErrors: string[];
  readonly isSubmitting: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4 mt-6">
      <FormError error={formErrors.length > 0 ? formErrors : null} icon="alert-circle" />
      <Button className="w-full" loading={isSubmitting} type="submit">
        {t('auth.login.signInButtonText')}
      </Button>
    </div>
  );
};

const LoginForm = ({
  onSubmit,
  forgotPasswordUrl,
}: {
  readonly onSubmit: SubmitHandler<Inputs>;
  readonly forgotPasswordUrl: string;
}) => {
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();
  const [showPassword, setShowPassword] = useState<boolean>();
  const passwordInputType = showPassword ? 'text' : 'password';
  const { t } = useTranslation();
  const submitHandler = handleSubmit(createSubmitHandler({ onSubmit, setError }));
  const formErrors = collectFormErrors(errors, t);

  return (
    <form onSubmit={submitHandler}>
      <FormFields
        errors={errors}
        forgotPasswordUrl={forgotPasswordUrl}
        passwordInputType={passwordInputType}
        passwordLess={false}
        register={register}
        setShowPassword={setShowPassword}
      />
      <FormActions formErrors={formErrors} isSubmitting={isSubmitting} />
    </form>
  );
};

export { LoginForm };
